import { useState, useCallback, useEffect, useContext } from 'react';
import { IApiHooksWithTrigger, IIdentityObject } from '../../interfaces/app-interfaces';
import { IMessage } from '../../interfaces/component-interfaces';
import { CAContext } from '../../contexts/CAContext';
import { useIdentity } from '@vp/auth-react';
import { transformStartChatApiResponse } from '../../utils/chatbot/api-utils';
import { ICBStartChatSessionResponse } from '../../interfaces/chatbot-api-interfaces';
import { API_ENDPOINTS } from '../../config/api-config';
import { ChatbotContext } from '../../contexts/ChatbotContext';
import { clearAllSessionExpiryData, getRestId, setChatbotToken } from '../../utils/session-storage-utils';
import { useNRLogging } from '../custom-hooks/useNRLogging';
import { getBrowserName, getDeviceType, getOperatingSystem } from '../../utils/app-utils';
import { util } from '../../utils/miaw/common';
import { DISPLAY_NAME } from '../../config/app-config';
import { useCustomTracking } from '../custom-hooks/useTracking';
import { CaseDataContext } from '../../contexts/CaseDataContext';
import { isFeatureEnabled } from '@vp/ab-reader';

export interface IChatbotStartData {
  sessionId: string;
  discussionId: string;
  message: IMessage;
}

const useCBChatStart = (): IApiHooksWithTrigger<IChatbotStartData> => {
  const { logCustomAttributesNR } = useNRLogging();

  const { sendTrackingEvent } = useCustomTracking();
  const identity = useIdentity();
  const {
    chatState: { gcpChatData, userInfo, locale },
    setChatState,
  } = useContext(CAContext);
  const {
    state: { pageInfo, caseData },
  } = useContext(CaseDataContext);

  const { setState: setChatbotContext } = useContext(ChatbotContext);
  const { setState: setChatbotState } = useContext(ChatbotContext);
  const [data, setData] = useState<IChatbotStartData>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  const [toggle, setToggle] = useState<boolean>(false);
  const trigger = useCallback(() => {
    setToggle((prev) => !prev);
  }, []);

  const { isSignedIn } = identity as IIdentityObject;
  // const hurricaneMessage = `**Just a heads up:** Specialist availability may be limited right now as Hurricane Melissa is affecting one of our contact centers. Thanks for your patience.`;
  const startChatMessage = isSignedIn
    ? [
        `Hi ${userInfo?.userData?.firstName || ''}! I’m Vista Assistant, your AI-powered helper. I can answer questions about your orders, designs, or anything related to the website.`,
        'How can I help you today?',
      ]
    : [
        `Hi! I’m Vista Assistant, your AI-powered helper. I can answer questions about your orders, designs, or anything related to the website.\n\nTip: You'll get help faster if you log in to your account.\n\n<ext>Sign in</ext>`,
        'How can I help you today?',
      ];

  // /** Only show hurricane message for specific locales */
  // if (locale === 'en-us' || locale === 'en-ca') {
  //   startChatMessage.splice(1, 0, hurricaneMessage); // From index 1, delete 0 elements and then add hurricane message
  // }
  useEffect(() => {
    const fetchData = async () => {
      if (toggle) {
        try {
          setLoading(true);
          // TODO : Will be removed once API is fast enough to return a message
          setChatbotContext((prev) => ({
            ...prev,
            showErrorMessage: false,
            conversationMessages: [
              {
                id: util.generateUUID(),
                timestamp: new Date().getTime(),
                messageContent: startChatMessage,
                type: 'chatbot',
                senderDisplayName: DISPLAY_NAME.CHATBOT,
                isMessageRead: false,
                showFeedback: false,
              },
            ],
          }));
          const { authorizationHeader, canonicalId } = identity as IIdentityObject;
          const myHeaders = new Headers();
          myHeaders.append('Authorization', authorizationHeader || '');
          myHeaders.append('Content-Type', 'application/json');
          const requestOptions = {
            method: 'POST',
            headers: myHeaders,
            body: JSON.stringify({
              shopper_id: canonicalId || '',
              chatLocale: locale,
              pageName: pageInfo?.pageName || 'Help Center:Home Page',
              pageURL: window?.location.href,
              caseOrigin: `${caseData?.caseOrigin || ''}-Chatbot`,
              status: caseData?.status || '',
              pageSection: pageInfo?.pageSection || 'Help Center',
              pageStage: pageInfo?.pageStage || 'Customer Care',
              pagePath: window?.location.pathname,
              isChatEnabled: gcpChatData?.status.isChatEnabled || false,
              userInfo: {
                isLoggedIn: userInfo?.isLoggedIn || false,
                firstName: userInfo?.userData?.firstName || undefined,
                lastName: userInfo?.userData?.lastName || undefined,
                isEmodeEnabled: gcpChatData?.status.isEmodeEnabled || false,
              },
              request_id: getRestId() || '',
              deviceType: getDeviceType() || '',
              browser: getBrowserName() || '',
              operatingSystem: getOperatingSystem() || '',
            }),
          };
          logCustomAttributesNR('ca-chatbot-api-request', {
            logLevel: 'info',
            message: 'Starting chatbot session',
            apiType: 'startChatbotSession',
            chatStatus: gcpChatData?.status?.isChatEnabled,
            isEmodeEnabled: gcpChatData?.status.isEmodeEnabled || false,
          });
          const res = await fetch(`${API_ENDPOINTS.CHATBOT_BASE_URL()}/start-session`, requestOptions);
          if (!res.ok) {
            throw new Error('Failed to fetch data');
          }
          const responseData = await res.json();
          const { discussionId, message, sessionId, chatbotToken } = transformStartChatApiResponse(responseData as ICBStartChatSessionResponse);
          // Set Token in localStorage
          setChatbotToken(chatbotToken, isSignedIn);
          setChatbotState((prev) => ({
            ...prev,
            showHelperButton: true,
          }));
          setChatState((prev) => ({
            ...prev,
            chatSession: 'chatbotSession',
            isProductAgentEnabled: isFeatureEnabled('chatbot-product-agent'),
          }));
          setData({ message, discussionId, sessionId });
          /** Clear all session expiry data once a new chat is started */
          clearAllSessionExpiryData();
          sendTrackingEvent('Chatbot Clicked', 'Chatbot:Session', 'chatSessionStarted');
          setError(undefined);
          logCustomAttributesNR('ca-chatbot-api-response', {
            logLevel: 'info',
            message: 'Starting chatbot session API success',
            apiType: 'startChatbotSession',
          });
          // setChatState((prev) => ({ chatSession: 'chatbotSession', ...prev }));
        } catch (err) {
          console.error(err);
          setError(err as Error);
          logCustomAttributesNR('ca-chatbot-api-error', {
            errorName: err.name,
            errorStack: err.stack,
            message: err.message,
            logLevel: 'error',
            apiType: 'startChatbotSession',
          });
        } finally {
          setLoading(false);
          setToggle(false); // Reset toggle to false after fetching
        }
      }
    };
    fetchData();
  }, [toggle]);

  return { data, error, loading, trigger };
};

export { useCBChatStart };
